import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Briefcase, MapPin, Globe, Linkedin, Instagram, Phone, Mail, Edit2, Trash2, X, Check, MessageSquare, User, ShieldCheck, Trophy, Search } from 'lucide-react';
import { toast } from 'sonner';

// ────────────────────────────────────────────────────────────────────────
// EDIT FORM MODAL
// ────────────────────────────────────────────────────────────────────────
function EditProfileModal({ open, initial, categories, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState(initial || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial || {}); }, [initial, open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!(form.profession || '').trim() || (form.profession || '').length < 2) return toast.error('Profession is required');
    if (!(form.description || '').trim() || (form.description || '').length < 10) return toast.error('Description must be at least 10 characters');
    setSaving(true);
    try {
      const res = await fetch(`${API}/business/profile`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profession: form.profession.trim(),
          category: form.category || 'Other',
          company: form.company || null,
          description: form.description.trim(),
          city: form.city || null,
          state: form.state || null,
          website: form.website || null,
          instagram: form.instagram || null,
          linkedin: form.linkedin || null,
          logo_url: form.logo_url || null,
          phone: form.phone || null,
          email_public: form.email_public || null,
        }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success('Business profile saved');
        onSaved(d.profile);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove your business profile? You can rebuild it any time.')) return;
    const res = await fetch(`${API}/business/profile`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) {
      toast.success('Profile removed');
      onDeleted();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl my-0 sm:my-4 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="business-edit-modal"
      >
        <div className="sticky top-0 bg-bundle-green text-white px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <h2 className="font-heading font-bold text-base">{initial?.profession ? 'Edit Business Profile' : 'Create Business Profile'}</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="business-edit-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <Field label="Profession *" testid="biz-profession">
            <input
              required
              value={form.profession || ''}
              onChange={(e) => setForm({ ...form, profession: e.target.value })}
              placeholder="e.g. Real Estate Agent"
              maxLength={80}
              data-testid="biz-profession-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-bundle-green focus:outline-none"
            />
          </Field>
          <Field label="Category">
            <select
              value={form.category || 'Other'}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="biz-category-select"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:border-bundle-green focus:outline-none"
            >
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Company / Brand">
            <input
              value={form.company || ''}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              placeholder="e.g. Hawkins Realty Group"
              maxLength={120}
              data-testid="biz-company-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-bundle-green focus:outline-none"
            />
          </Field>
          <Field label="Description * (10-600 chars)">
            <textarea
              required
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What you do, who you serve, how members can reach you. Keep it clean — no spam."
              minLength={10}
              maxLength={600}
              rows={4}
              data-testid="biz-description-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-bundle-green focus:outline-none"
            />
            <p className="text-[10px] text-gray-500 mt-1">{(form.description || '').length}/600</p>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City"><input value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} maxLength={80} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="biz-city-input" /></Field>
            <Field label="State"><input value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="MD" maxLength={40} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="biz-state-input" /></Field>
          </div>
          <Field label="Website"><input value={form.website || ''} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" maxLength={200} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="biz-website-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Instagram handle"><input value={form.instagram || ''} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="@yourname" maxLength={80} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="biz-ig-input" /></Field>
            <Field label="LinkedIn URL"><input value={form.linkedin || ''} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder="linkedin.com/in/…" maxLength={200} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="biz-li-input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Public Phone (optional)"><input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} maxLength={30} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="biz-phone-input" /></Field>
            <Field label="Public Email (optional)"><input value={form.email_public || ''} onChange={(e) => setForm({ ...form, email_public: e.target.value })} maxLength={120} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="biz-email-input" /></Field>
          </div>
          <Field label="Logo / Photo URL (optional)"><input value={form.logo_url || ''} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://…" maxLength={500} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="biz-logo-input" /></Field>

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving} className="flex-1 bg-bundle-green hover:bg-bundle-green-dark text-white font-bold py-2.5 rounded-lg text-sm disabled:opacity-50" data-testid="biz-save-btn">
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
            {initial?.profession && (
              <button type="button" onClick={remove} className="px-4 py-2.5 border border-red-300 text-red-600 hover:bg-red-50 rounded-lg text-sm font-semibold" data-testid="biz-delete-btn">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

// ────────────────────────────────────────────────────────────────────────
// PROFILE CARD
// ────────────────────────────────────────────────────────────────────────
function ProfileCard({ profile, onMessage, onView, onViewMember }) {
  const initials = (profile.display_name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const memberYear = profile.member_since ? new Date(profile.member_since).getFullYear() : null;

  return (
    <div
      className="bg-white border border-gray-200 hover:border-bundle-green/40 rounded-xl shadow-sm hover:shadow-md transition-all p-4 flex flex-col"
      data-testid={`biz-card-${profile.user_id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        {profile.logo_url ? (
          <img src={profile.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover border border-gray-200 flex-shrink-0" />
        ) : profile.picture ? (
          <img src={profile.picture} alt="" className="w-12 h-12 rounded-full object-cover border border-gray-200 flex-shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-bundle-green text-white font-heading font-bold flex items-center justify-center flex-shrink-0">{initials}</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-heading font-bold text-bundle-green text-sm truncate">{profile.display_name}</p>
            {profile.is_active_member && (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full" title="Active Bundle member">
                <ShieldCheck className="w-2.5 h-2.5" /> Active
              </span>
            )}
          </div>
          <p className="text-xs text-gray-700 font-semibold truncate">{profile.profession}</p>
          {profile.company && <p className="text-[11px] text-gray-500 truncate">{profile.company}</p>}
        </div>
      </div>

      {(profile.city || profile.state) && (
        <div className="flex items-center gap-1 text-[11px] text-gray-500 mb-2">
          <MapPin className="w-3 h-3" />
          <span>{[profile.city, profile.state].filter(Boolean).join(', ')}</span>
        </div>
      )}
      <p className="text-xs text-gray-700 line-clamp-3 mb-3">{profile.description}</p>

      <div className="flex items-center gap-3 text-[10px] text-gray-500 mb-3 flex-wrap">
        <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-full">
          <Briefcase className="w-2.5 h-2.5" /> {profile.category}
        </span>
        {(profile.rounds_count || 0) > 0 && (
          <span className="inline-flex items-center gap-1 bg-bundle-gold/15 text-amber-700 px-2 py-0.5 rounded-full" title="Rounds posted">
            <Trophy className="w-2.5 h-2.5" /> {profile.rounds_count} rds
          </span>
        )}
        {memberYear && <span>Member since {memberYear}</span>}
      </div>

      <div className="flex gap-2 mt-auto">
        <button
          type="button"
          onClick={() => onView(profile)}
          data-testid={`biz-view-btn-${profile.user_id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 hover:border-bundle-green/40 text-gray-700 hover:text-bundle-green rounded-lg text-xs font-semibold"
        >
          <Briefcase className="w-3.5 h-3.5" /> Business Info
        </button>
        <button
          type="button"
          onClick={() => onMessage(profile)}
          data-testid={`biz-msg-btn-${profile.user_id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-bundle-green hover:bg-bundle-green-dark text-white rounded-lg text-xs font-semibold"
        >
          <MessageSquare className="w-3.5 h-3.5" /> Message
        </button>
      </div>

      <button
        type="button"
        onClick={() => onViewMember(profile)}
        data-testid={`biz-view-member-btn-${profile.user_id}`}
        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-bundle-gold/15 hover:bg-bundle-gold/25 text-amber-800 hover:text-amber-900 rounded-lg text-xs font-bold border border-bundle-gold/40 transition-colors"
      >
        <User className="w-3.5 h-3.5" /> View {profile.display_name?.split(' ')[0] || 'Member'}'s Bundle Profile →
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// FULL PROFILE DETAIL MODAL
// ────────────────────────────────────────────────────────────────────────
function ProfileDetailModal({ profile, onClose, onMessage, onViewMember }) {
  if (!profile) return null;
  const initials = (profile.display_name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const links = [
    { icon: Globe, label: 'Website', href: profile.website },
    { icon: Linkedin, label: 'LinkedIn', href: profile.linkedin?.startsWith('http') ? profile.linkedin : `https://${profile.linkedin}` },
    { icon: Instagram, label: profile.instagram, href: profile.instagram ? `https://instagram.com/${profile.instagram.replace('@', '')}` : null },
    { icon: Phone, label: profile.phone, href: profile.phone ? `tel:${profile.phone}` : null },
    { icon: Mail, label: profile.email_public, href: profile.email_public ? `mailto:${profile.email_public}` : null },
  ].filter(l => l.href);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl my-0 sm:my-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="biz-detail-modal">
        <div className="sticky top-0 bg-bundle-green text-white px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <h2 className="font-heading font-bold text-base">Business Profile</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4">
          <div className="flex items-start gap-3 mb-4">
            {profile.logo_url ? (
              <img src={profile.logo_url} alt="" className="w-16 h-16 rounded-xl object-cover border" />
            ) : profile.picture ? (
              <img src={profile.picture} alt="" className="w-16 h-16 rounded-full object-cover border" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-bundle-green text-white text-xl font-heading font-bold flex items-center justify-center">{initials}</div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-bundle-green text-lg leading-tight">{profile.display_name}</p>
              <p className="text-sm font-semibold text-gray-800">{profile.profession}</p>
              {profile.company && <p className="text-xs text-gray-500">{profile.company}</p>}
              {(profile.city || profile.state) && (
                <p className="text-xs text-gray-500 inline-flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" /> {[profile.city, profile.state].filter(Boolean).join(', ')}
                </p>
              )}
            </div>
          </div>

          <p className="text-sm text-gray-700 whitespace-pre-line mb-4">{profile.description}</p>

          {links.length > 0 && (
            <div className="border-t pt-3 space-y-2 mb-4">
              {links.map(({ icon: Icon, label, href }, i) => (
                <a key={i} href={href} target={href.startsWith('http') ? '_blank' : '_self'} rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-bundle-green hover:underline">
                  <Icon className="w-4 h-4 flex-shrink-0" /><span className="truncate">{label}</span>
                </a>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 text-center text-xs border-t pt-3 mb-4">
            <div><p className="font-bold text-bundle-green">{profile.rounds_count || 0}</p><p className="text-[10px] text-gray-500">Rounds</p></div>
            <div><p className="font-bold text-bundle-green">{profile.is_active_member ? 'Active' : 'Inactive'}</p><p className="text-[10px] text-gray-500">Member</p></div>
            <div><p className="font-bold text-bundle-green">{profile.member_since ? new Date(profile.member_since).getFullYear() : '—'}</p><p className="text-[10px] text-gray-500">Since</p></div>
          </div>

          <button
            type="button"
            onClick={() => onMessage(profile)}
            className="w-full bg-bundle-green hover:bg-bundle-green-dark text-white font-bold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2"
            data-testid="biz-detail-msg-btn"
          >
            <MessageSquare className="w-4 h-4" /> Message {profile.display_name?.split(' ')[0]}
          </button>

          <button
            type="button"
            onClick={() => onViewMember(profile)}
            className="mt-2 w-full bg-bundle-gold/15 hover:bg-bundle-gold/25 text-amber-800 hover:text-amber-900 font-bold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 border border-bundle-gold/40 transition-colors"
            data-testid="biz-detail-view-member-btn"
          >
            <User className="w-4 h-4" /> View {profile.display_name?.split(' ')[0]}'s Bundle Profile →
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ────────────────────────────────────────────────────────────────────────
export default function BusinessNetwork() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [profiles, setProfiles] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [myProfile, setMyProfile] = useState(null);
  const [filters, setFilters] = useState({ category: 'All', state: '', city: '', search: '' });
  const [editOpen, setEditOpen] = useState(false);
  const [detailProfile, setDetailProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category && filters.category !== 'All') params.set('category', filters.category);
      if (filters.state) params.set('state', filters.state);
      if (filters.city) params.set('city', filters.city);
      if (filters.search) params.set('search', filters.search);
      const [list, mine] = await Promise.all([
        fetch(`${API}/business/profiles?${params}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/business/me`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setProfiles(list.profiles || []);
      setMyProfile(mine && mine.profession ? mine : null);
    } catch (e) {
      console.error('biz fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetch(`${API}/business/categories`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setCategories(['All', ...(d.categories || [])]));
  }, []);

  useEffect(() => { if (user) fetchAll(); }, [user, fetchAll]);

  // Auto-open edit if URL has ?edit=1
  useEffect(() => {
    if (new URLSearchParams(location.search).get('edit') === '1') setEditOpen(true);
  }, [location.search]);

  const messageMember = (p) => {
    navigate('/chat', {
      state: {
        startDmWith: { user_id: p.user_id, name: p.display_name },
        prefillMessage: `Hey ${p.display_name?.split(' ')[0]}, saw your business profile on the Bundle network — wanted to connect.`,
      },
    });
  };

  const viewMemberProfile = (p) => {
    // Deep link to Member Directory and auto-open this member's profile modal
    navigate(`/members?openMember=${encodeURIComponent(p.user_id)}`);
  };

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav">
      <Navigation user={user} />
      <div className="max-w-5xl mx-auto px-4 pt-20 md:pt-24 pb-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Briefcase className="w-6 h-6 text-bundle-green" />
          <div>
            <h1 className="text-2xl font-heading font-bold text-bundle-green leading-tight">Business to Business Network</h1>
            <p className="text-xs text-gray-500">Trusted connections from your Bundle community.</p>
          </div>
        </div>

        {/* Your card / CTA */}
        <div className="bg-white border border-bundle-green/30 rounded-xl p-4 mb-5 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-bundle-green font-bold">Your Listing</p>
            <p className="text-sm font-semibold text-gray-800 truncate">
              {myProfile ? `${myProfile.profession}${myProfile.company ? ' · ' + myProfile.company : ''}` : "You don't have a business profile yet."}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            data-testid="biz-edit-mine-btn"
            className="bg-bundle-green hover:bg-bundle-green-dark text-white font-bold px-4 py-2 rounded-lg text-xs flex items-center gap-1.5"
          >
            {myProfile ? <><Edit2 className="w-3.5 h-3.5" />Edit</> : <><Check className="w-3.5 h-3.5" />Create Profile</>}
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-3 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search profession, company…"
                data-testid="biz-search-input"
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              data-testid="biz-filter-category"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
              value={filters.state}
              onChange={(e) => setFilters({ ...filters, state: e.target.value })}
              placeholder="State (e.g. MD)"
              data-testid="biz-filter-state"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              placeholder="City"
              data-testid="biz-filter-city"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white border rounded-xl p-4 animate-pulse">
                <div className="h-12 w-12 rounded-full bg-gray-200 mb-3" />
                <div className="h-3 bg-gray-200 rounded mb-2" />
                <div className="h-3 bg-gray-100 rounded w-2/3 mb-3" />
                <div className="h-12 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No business profiles match these filters yet.</p>
            {!myProfile && <p className="text-xs text-gray-500 mt-1">Be the first — tap "Create Profile" above.</p>}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="biz-grid">
            {profiles.map(p => (
              <ProfileCard key={p.user_id} profile={p} onMessage={messageMember} onView={setDetailProfile} onViewMember={viewMemberProfile} />
            ))}
          </div>
        )}
      </div>

      <EditProfileModal
        open={editOpen}
        initial={myProfile}
        categories={categories.filter(c => c !== 'All')}
        onClose={() => setEditOpen(false)}
        onSaved={(p) => { setMyProfile(p); setEditOpen(false); fetchAll(); }}
        onDeleted={() => { setMyProfile(null); setEditOpen(false); fetchAll(); }}
      />
      <ProfileDetailModal profile={detailProfile} onClose={() => setDetailProfile(null)} onMessage={messageMember} onViewMember={viewMemberProfile} />
    </div>
  );
}
